// Per-line item entity tagging at Import Control (associated-entities.md → AE4).
// Tags a line item with the individual entities it spends on — Vehicle /
// Staff / Participant — for later per-entity cost rollups (AE5). Self-contained:
// each action is its own write (add / edit / remove) rather than riding the
// invoice form's save, so a mis-tag never blocks the invoice.
//
// Vehicle and Staff resolve through a picker into TYPED reference columns
// (api-contract §9): a Vehicle pick writes `vehicle_id`, the uuid of the
// `vehicles` row, and a Staff pick writes `staff_upn`. There is no single
// overloaded tag column — the schema's CHECK permits only the column matching
// `entity_type`, and the API ignores any other key, which is why the app's old
// `entity_id` made every hand-made tag a silent no-op (M9). `entity_name` carries
// the display value beside the reference (the fleet number, the person's name),
// which is what a resolved-by-worker row stores too, so a manual pick and a
// matched one read identically.
//
// The API stamps `match_source = 'Manual'` on every create and every edit here —
// a human touched the row. The pill still reads "Unmatched" when the row holds no
// reference, because that is the state a reviewer scanning for unresolved tags
// cares about (QA Q26); it is display logic over the data, not a second value.
//
// `entity_type` is FIXED at creation: §9.3 has no `entity_type` in its patch body,
// so on a saved row the type is shown as text rather than a dropdown that would
// look editable and change nothing.
//
// PORT NOTE — Participant is as-printed text only. The participant census was a
// direct read of a clinical SQL view; it is not part of this system, so there is
// no picker, no center scope and no resolution for a Participant tag. The
// document text is captured and that is the whole record.
//
// entity_source_text is the document text that triggered the reference,
// verbatim — an as-printed value, so like every other as-printed value in the
// schema it is never overwritten. On an existing row it is read-only: overtyping
// it would destroy the capture record and the key Staff/Vehicle resolution
// matches on. The add form still takes it as free text — a hand-added row has no
// document text to preserve, and source text is how an unresolvable reference
// gets recorded at all.
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Body1,
  Button,
  Caption1,
  Dropdown,
  Input,
  Option,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { AddRegular, DeleteRegular } from '@fluentui/react-icons'
import type { AssociatedEntity, InvoiceLineItem, Invoice } from '../lib/types'
import {
  createAssociatedEntity,
  deactivateAssociatedEntity,
  loadAssociatedEntities,
  searchStaff,
  searchVehicles,
  updateAssociatedEntity,
} from '../lib/data'
import { errorMessage } from '../api/errors'
import { DotPill } from './Badges'
import { EntityPicker, type EntityOption } from './EntityPicker'
import { popupPositionProps, usePopupListboxClass } from './popupPositioning'
import { TINT_PILLS } from '../lib/theme'

const ENTITY_TYPES = ['Vehicle', 'Staff', 'Participant'] as const
type EntityType = (typeof ENTITY_TYPES)[number]

/** Participant has no picker — nothing resolves a participant here. */
function hasPicker(type: EntityType): boolean {
  return type !== 'Participant'
}

// The picker's search, chosen by the row's entity type (searchVehicles /
// searchStaff already return the EntityPicker option shape).
function searchForType(type: EntityType): (term: string) => Promise<EntityOption[]> {
  if (type === 'Staff') return (t) => searchStaff(t)
  return (t) => searchVehicles(t)
}

function pickerPlaceholder(type: EntityType): string {
  return type === 'Staff' ? 'Search staff…' : 'Search vehicles…'
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px 12px',
    marginTop: '4px',
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
  head: { display: 'flex', alignItems: 'center', gap: '8px' },
  headLabel: {
    fontSize: '10.5px',
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3,
  },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  list: { display: 'flex', flexDirection: 'column', gap: '6px' },
  // A tagged-entity row: type | source text | matched name | source pill | actions.
  row: {
    display: 'grid',
    gridTemplateColumns: '128px minmax(0, 1fr) minmax(0, 1fr) auto auto',
    alignItems: 'center',
    gap: '6px',
  },
  cell: { width: '100%', minWidth: 0 },
  // Read-only as-printed source text on an existing row. Sized/aligned like the
  // inputs beside it so the row still reads as one line.
  sourceText: {
    minWidth: 0,
    padding: '0 4px',
    fontSize: tokens.fontSizeBase200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sourceTextNone: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // Stands in for the picker on a Participant row, which has no lookup.
  noPicker: {
    minWidth: 0,
    padding: '0 4px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  typeDropdown: { width: '100%', minWidth: 0 },
  rowActions: { display: 'flex', alignItems: 'center', gap: '2px' },
  error: { color: tokens.colorPaletteRedForeground1 },
})

const matchSourcePill = (source: string | undefined) =>
  !source || source === 'Unmatched' ? TINT_PILLS.yellow : TINT_PILLS.green

function typeOf(v: string | undefined): EntityType {
  return (ENTITY_TYPES as readonly string[]).includes(v ?? '') ? (v as EntityType) : 'Vehicle'
}

/** The reference this row resolved to, read out of the column its type uses.
    A Participant row never has one — that is the whole design, not a gap. */
function referenceIdOf(ae: AssociatedEntity): string {
  const type = typeOf(ae.entity_type)
  if (type === 'Vehicle') return ae.vehicle_id ?? ''
  if (type === 'Staff') return ae.staff_upn ?? ''
  return ''
}

/** The typed reference column for `type`, as a write body. `null` clears the
    pick; the column for the OTHER type is sent as null on an edit so a row can
    never hold two references (the schema CHECK refuses it outright). */
function referenceFields(
  type: EntityType,
  entityId: string | undefined,
  mode: 'create' | 'edit',
): { vehicle_id?: string | null; staff_upn?: string | null } {
  const id = entityId || null
  if (type === 'Vehicle') return mode === 'create' && id === null ? {} : { vehicle_id: id }
  if (type === 'Staff') return mode === 'create' && id === null ? {} : { staff_upn: id }
  return {} // Participant: as-printed text only, no reference column
}

/** What the pill should say. The API records that a human set the row; a row with
    no reference is still unresolved, whatever set it (QA Q26). */
function displayedMatchSource(ae: AssociatedEntity): string {
  if (typeOf(ae.entity_type) === 'Participant') return ae.match_source ?? 'Unmatched'
  return referenceIdOf(ae) ? (ae.match_source ?? 'Manual') : 'Unmatched'
}

interface Props {
  invoice: Invoice
  lineItem: InvoiceLineItem
}

export function AssociatedEntitiesEditor({ invoice, lineItem }: Props) {
  const s = useStyles()
  // Shared height/z-index for the entity-type Dropdown's listbox below — see
  // `popupPositioning.ts`. This is the deterministic break defect: this
  // editor opens inside a `PopoverSurface` (`entityPopSurface`, `zIndex: 10`
  // in `EditableInvoiceForm.tsx`) mounted at the same dialog host, so without
  // this the listbox is a z-auto sibling of that opaque z-10 surface and
  // renders behind its own popover.
  const popupListboxClass = usePopupListboxClass()
  const headerId = invoice.id ?? ''
  const childId = lineItem.id
  const [rows, setRows] = useState<AssociatedEntity[] | 'loading'>('loading')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Inline add form.
  const [adding, setAdding] = useState(false)
  const [addType, setAddType] = useState<EntityType>('Vehicle')
  const [addSource, setAddSource] = useState('')
  // The picked entity (id + display name) from the type-conditional picker.
  const [addEntity, setAddEntity] = useState<{ id?: string; name?: string }>({})
  // Monotonic load id so a slow refresh can't overwrite a newer one.
  const loadSeq = useRef(0)
  // This editor lives inside a Popover off the row's tag button, itself mounted
  // at the invoice Dialog's own surface (`dialogEl` in EditableInvoiceForm) once
  // that resolves — so `.closest` from anywhere in here finds the SAME dialog
  // host the entity pickers below need for `popupPositionProps`. Resolve it
  // independently rather than threading a prop down: this component has no
  // other reason to know about its parent's dialog.
  const rootRef = useRef<HTMLDivElement>(null)
  const [dialogHost, setDialogHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    // `role="dialog"` only — see `EntityPicker.tsx`'s matching comment on why
    // this does not also match `role="alertdialog"`.
    setDialogHost(rootRef.current?.closest('[role="dialog"]') as HTMLElement | null)
  }, [])

  const refresh = useCallback(async () => {
    const seq = ++loadSeq.current
    if (!headerId) {
      setRows([])
      return
    }
    try {
      const all = await loadAssociatedEntities(headerId)
      if (seq !== loadSeq.current) return
      setRows(all.filter((e) => e.child_id === childId))
    } catch (e) {
      if (seq !== loadSeq.current) return
      setRows([])
      setError(`Could not load entities: ${errorMessage(e)}`)
    }
  }, [headerId, childId])

  useEffect(() => {
    refresh()
  }, [refresh])

  // PORT NOTE: each action used to also write its own correction row for the
  // feedback loop. The API writes corrections itself now, from the change it
  // applied, so there is nothing to mirror here.

  async function handleAdd() {
    const type = addType
    const source = addSource.trim()
    const entityId = addEntity.id
    const entityName = addEntity.name
    if (!source && !entityId) return // nothing meaningful to tag
    setBusy(true)
    setError(null)
    try {
      await createAssociatedEntity(headerId, {
        child_id: childId,
        entity_type: type,
        entity_source_text: source || undefined,
        entity_name: entityName || undefined,
        // The typed reference column for this type, and only that one.
        // `match_source` is not sent: §9.2 stamps 'Manual' on anything created
        // here, because a hand-added row is by definition a human's.
        ...referenceFields(type, entityId, 'create'),
      })
      setAdding(false)
      setAddType('Vehicle')
      setAddSource('')
      setAddEntity({})
      await refresh()
    } catch (e) {
      setError(`Could not add entity: ${errorMessage(e)}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleSaveRow(
    ae: AssociatedEntity,
    draft: { entity: { id?: string; name?: string } },
  ) {
    // The row's own type: §9.3 cannot change it, so the edit is the reference and
    // its display name. `entity_source_text` is not sent AT ALL — its mere
    // presence in a patch body is a 400, because it is as-printed provenance.
    const type = typeOf(ae.entity_type)
    const entityId = draft.entity.id ?? ''
    const entityName = draft.entity.name ?? ''
    setBusy(true)
    setError(null)
    try {
      await updateAssociatedEntity(ae.id, {
        // Empty clears the pick; a populated id resolves the row.
        ...referenceFields(type, entityId, 'edit'),
        entity_name: entityName,
        row_version: ae.row_version,
      })
      await refresh()
    } catch (e) {
      setError(`Could not save entity: ${errorMessage(e)}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleRemove(ae: AssociatedEntity) {
    setBusy(true)
    setError(null)
    try {
      await deactivateAssociatedEntity(ae.id, ae.row_version)
      await refresh()
    } catch (e) {
      setError(`Could not remove entity: ${errorMessage(e)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={s.root} ref={rootRef}>
      <div className={s.head}>
        <span className={s.headLabel}>Tagged entities</span>
        {rows === 'loading' && <Spinner size="tiny" />}
      </div>

      {error && <Caption1 className={s.error}>{error}</Caption1>}

      {rows !== 'loading' && rows.length === 0 && !adding && (
        <Body1 className={s.none}>No entities tagged on this line.</Body1>
      )}

      {rows !== 'loading' && rows.length > 0 && (
        <div className={s.list}>
          {rows.map((ae) => (
            <EntityRow
              key={ae.id}
              ae={ae}
              busy={busy}
              onSave={(draft) => handleSaveRow(ae, draft)}
              onRemove={() => handleRemove(ae)}
            />
          ))}
        </div>
      )}

      {adding ? (
        <div className={s.row}>
          <Dropdown
            className={s.typeDropdown}
            size="small"
            {...popupPositionProps(dialogHost)}
            listbox={{ className: popupListboxClass }}
            value={addType}
            selectedOptions={[addType]}
            // Changing type invalidates any pick (a vehicle number is not a valid
            // staff UPN etc.) — clear it so the picker re-resolves for the new type.
            onOptionSelect={(_, d) => { setAddType(typeOf(d.optionValue)); setAddEntity({}) }}
          >
            {ENTITY_TYPES.map((t) => (
              <Option key={t} value={t}>{t}</Option>
            ))}
          </Dropdown>
          <Input
            className={s.cell}
            size="small"
            placeholder="Source text (e.g. bus 24)"
            value={addSource}
            onChange={(_, d) => setAddSource(d.value)}
          />
          {hasPicker(addType) ? (
            <EntityPicker
              key={addType}
              size="small"
              ariaLabel={`${addType} to tag`}
              value={addEntity}
              placeholder={pickerPlaceholder(addType)}
              search={searchForType(addType)}
              onSelect={(o) => setAddEntity({ id: o.id, name: o.primary })}
            />
          ) : (
            <span className={s.noPicker}>As printed — no lookup</span>
          )}
          <Button size="small" appearance="primary" disabled={busy || (!addSource.trim() && !addEntity.id)} onClick={handleAdd}>
            Add
          </Button>
          <Button size="small" appearance="subtle" disabled={busy} onClick={() => { setAdding(false); setAddSource(''); setAddEntity({}); setAddType('Vehicle') }}>
            Cancel
          </Button>
        </div>
      ) : (
        rows !== 'loading' && (
          <div>
            <Button size="small" appearance="subtle" icon={<AddRegular />} disabled={busy} onClick={() => setAdding(true)}>
              Add entity
            </Button>
          </div>
        )
      )}
    </div>
  )
}

// A single existing tagged-entity row with its own draft state (seeded once from
// the loaded row). The Save button lights up only when the draft differs.
function EntityRow({
  ae,
  busy,
  onSave,
  onRemove,
}: {
  ae: AssociatedEntity
  busy: boolean
  onSave: (draft: { entity: { id?: string; name?: string } }) => void
  onRemove: () => void
}) {
  const s = useStyles()
  // FIXED, not a draft: §9.3 carries no `entity_type`, so the type a row was
  // created with is the type it keeps.
  const type = typeOf(ae.entity_type)
  // As-printed, never edited here — read straight off the row rather than held
  // as draft state, so a save can only ever write back the captured value.
  const source = ae.entity_source_text ?? ''
  const [entity, setEntity] = useState<{ id?: string; name?: string }>(() => ({
    id: referenceIdOf(ae) || undefined,
    name: ae.entity_name || undefined,
  }))

  // Adopt server-side changes (e.g. the directory worker resolving an entity
  // after load) into the draft — but ONLY while the draft still matched the
  // PREVIOUS server row, so an in-progress edit is never clobbered.
  const prevAeRef = useRef(ae)
  useEffect(() => {
    const prev = prevAeRef.current
    prevAeRef.current = ae
    const serverChanged =
      referenceIdOf(ae) !== referenceIdOf(prev) ||
      (ae.entity_name ?? '') !== (prev.entity_name ?? '')
    if (!serverChanged) return
    const wasUntouched =
      (entity.id ?? '') === referenceIdOf(prev) &&
      (entity.name ?? '') === (prev.entity_name ?? '')
    if (!wasUntouched) return
    setEntity({ id: referenceIdOf(ae) || undefined, name: ae.entity_name || undefined })
  }, [ae, entity])

  const dirty =
    (entity.id ?? '') !== referenceIdOf(ae) ||
    (entity.name ?? '') !== (ae.entity_name ?? '')

  return (
    <div className={s.row}>
      <span className={s.sourceText} title={`Entity type: ${type}`}>
        {type}
      </span>
      <span
        className={mergeClasses(s.sourceText, !source && s.sourceTextNone)}
        title={source || undefined}
      >
        {source || 'No source text'}
      </span>
      {hasPicker(type) ? (
        <EntityPicker
          key={type}
          size="small"
          ariaLabel={`${type} to tag`}
          value={entity}
          placeholder={pickerPlaceholder(type)}
          search={searchForType(type)}
          onSelect={(o) => setEntity({ id: o.id, name: o.primary })}
        />
      ) : (
        <span className={s.noPicker}>As printed — no lookup</span>
      )}
      <DotPill pill={matchSourcePill(displayedMatchSource(ae))} title={`Match source: ${displayedMatchSource(ae)}`}>
        {displayedMatchSource(ae)}
      </DotPill>
      <div className={s.rowActions}>
        {dirty && (
          <Button size="small" appearance="primary" disabled={busy} onClick={() => onSave({ entity })}>
            Save
          </Button>
        )}
        <Button size="small" appearance="subtle" icon={<DeleteRegular />} aria-label="Remove entity" disabled={busy} onClick={onRemove} />
      </div>
    </div>
  )
}
